import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
  useToast,
  VStack,
  Text,
  Box,
  Code,
} from "@chakra-ui/react";
import { User } from "core";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import React, { useEffect, useState } from "react";
import { useFirestore, useFunctions } from "reactfire";

interface CreateAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  onSuccess: () => void;
}

export default function CreateAnnouncementModal({
  isOpen,
  onClose,
  communityId,
  onSuccess,
}: CreateAnnouncementModalProps) {
  const functions = useFunctions();
  const firestore = useFirestore();
  const toast = useToast();

  const [message, setMessage] = useState("");
  const [targetMode, setTargetMode] = useState<"all" | "specific">("all");
  const [users, setUsers] = useState<(User & { uid: string })[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch users for the community
  useEffect(() => {
    async function fetchUsers() {
      if (!communityId) return;

      try {
        setLoadingUsers(true);
        const usersQuery = query(
          collection(firestore, "users"),
          where("communityId", "==", communityId)
        );
        const usersSnapshot = await getDocs(usersQuery);

        const fetchedUsers = usersSnapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as (User & { uid: string })[];

        setUsers(fetchedUsers.filter((u) => u.phoneNumber)); // Only users with phone numbers
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoadingUsers(false);
      }
    }

    if (isOpen) {
      fetchUsers();
    }
  }, [communityId, isOpen, firestore]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({
        title: "Message required",
        description: "Please enter a message to send",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const createAnnouncementFn = httpsCallable(
        functions,
        "createAnnouncement"
      );

      const result = await createAnnouncementFn({
        communityId,
        message: message.trim(),
        targetUserIds:
          targetMode === "specific" && selectedUserIds.length > 0
            ? selectedUserIds
            : undefined,
      });

      const data = result.data as {
        success: boolean;
        announcementId: string;
        totalRecipients: number;
      };

      toast({
        title: "Announcement created",
        description: `Sending to ${data.totalRecipients} recipient(s)`,
        status: "success",
        duration: 5000,
      });

      setMessage("");
      setSelectedUserIds([]);
      setTargetMode("all");
      onSuccess();
    } catch (error: any) {
      console.error("Error creating announcement:", error);
      toast({
        title: "Error creating announcement",
        description: error.message || "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = Array.from(e.target.selectedOptions);
    const ids = options.map((option) => option.value);
    setSelectedUserIds(ids);
  };

  const getMessagePreview = () => {
    if (!message) return "";
    // Show preview with sample interpolation
    return message.replace(/\{name\}/gi, "John").replace(/\{firstName\}/gi, "John").replace(/\{fullName\}/gi, "John Doe");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Announcement</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Message</FormLabel>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hi {name}! This is an important announcement..."
                rows={6}
              />
              <FormHelperText>
                Use <Code>{"{name}"}</Code> or <Code>{"{firstName}"}</Code> for
                first name, <Code>{"{fullName}"}</Code> for full name
              </FormHelperText>
            </FormControl>

            {message && (
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" mb={1}>
                  Preview:
                </Text>
                <Text fontSize="sm" color="gray.700">
                  {getMessagePreview()}
                </Text>
              </Box>
            )}

            <FormControl>
              <FormLabel>Target Audience</FormLabel>
              <Select
                value={targetMode}
                onChange={(e) =>
                  setTargetMode(e.target.value as "all" | "specific")
                }
              >
                <option value="all">
                  All community members ({users.length})
                </option>
                <option value="specific">Select specific users</option>
              </Select>
            </FormControl>

            {targetMode === "specific" && (
              <FormControl>
                <FormLabel>Select Users</FormLabel>
                <Select
                  multiple
                  size="lg"
                  height="400px"
                  onChange={handleUserSelection}
                  value={selectedUserIds}
                  isDisabled={loadingUsers}
                >
                  {users.map((user) => (
                    <option key={user.uid} value={user.uid}>
                      {user.name} ({user.phoneNumber})
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  Hold Cmd/Ctrl to select multiple users. Selected:{" "}
                  {selectedUserIds.length}
                </FormHelperText>
              </FormControl>
            )}

            <Alert status="info" fontSize="sm">
              <AlertIcon />
              Messages will be sent asynchronously. Check the announcements list
              for progress updates.
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creating..."
          >
            Create & Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
